/**
 * FeedbackModal - Success/needs-work overlay for all activity types.
 *
 * Ported from bumblebee FeedbackModal. Shows star rating, confetti,
 * and action buttons (Replay, Next, Try Again).
 *
 * @see GitHub Issue #852
 */

import ActivityAudio from './ActivityAudio';

export default class FeedbackModal {
    constructor () {
        this._overlay = null;
        this._dialog = null;
        this._onReplay = null;
        this._onNext = null;
        this._onRetry = null;
    }

    /**
     * Show success feedback.
     * @param {object} opts
     * @param {number} opts.stars - 1-3 stars earned
     * @param {function} opts.onReplay - Replay callback
     * @param {function} opts.onNext - Next activity callback
     * @param {string} [opts.message] - Optional custom message
     */
    showSuccess (opts) {
        this.destroy();

        this._onReplay = opts.onReplay;
        this._onNext = opts.onNext;

        this._overlay = document.createElement('div');
        this._overlay.className = 'activity-feedback-overlay activity-feedback-success';

        this._dialog = document.createElement('div');
        this._dialog.className = 'activity-feedback-dialog';

        // Star display
        const starsRow = document.createElement('div');
        starsRow.className = 'activity-feedback-stars';
        for (let i = 1; i <= 3; i++) {
            const star = document.createElement('span');
            star.className = i <= opts.stars ? 'activity-star earned' : 'activity-star empty';
            star.textContent = i <= opts.stars ? '\u2605' : '\u2606';
            starsRow.appendChild(star);
        }
        this._dialog.appendChild(starsRow);

        // Message
        if (opts.message) {
            const msg = document.createElement('p');
            msg.className = 'activity-feedback-message';
            msg.textContent = opts.message;
            this._dialog.appendChild(msg);
        }

        // Confetti
        this._addConfetti();

        // Buttons
        const btnRow = document.createElement('div');
        btnRow.className = 'activity-feedback-buttons';

        const replayBtn = this._createButton('activity-btn-replay', '\u21BA', () => {
            this.destroy();
            if (this._onReplay) this._onReplay();
        });
        btnRow.appendChild(replayBtn);

        if (opts.onNext) {
            const nextBtn = this._createButton('activity-btn-next', '\u25B6', () => {
                this.destroy();
                if (this._onNext) this._onNext();
            });
            btnRow.appendChild(nextBtn);
        }

        this._dialog.appendChild(btnRow);
        this._overlay.appendChild(this._dialog);
        document.body.appendChild(this._overlay);

        ActivityAudio.play('victory');
    }

    /**
     * Show needs-work feedback.
     * @param {object} opts
     * @param {function} opts.onRetry - Retry callback
     * @param {string} [opts.message] - Encouragement message
     */
    showNeedsWork (opts) {
        this.destroy();

        this._onRetry = opts.onRetry;

        this._overlay = document.createElement('div');
        this._overlay.className = 'activity-feedback-overlay activity-feedback-needswork';

        this._dialog = document.createElement('div');
        this._dialog.className = 'activity-feedback-dialog';

        const msg = document.createElement('p');
        msg.className = 'activity-feedback-message';
        msg.textContent = opts.message || 'Almost! Try again!';
        this._dialog.appendChild(msg);

        const retryBtn = this._createButton('activity-btn-retry', '\u21BA', () => {
            this.destroy();
            if (this._onRetry) this._onRetry();
        });
        this._dialog.appendChild(retryBtn);

        this._overlay.appendChild(this._dialog);
        document.body.appendChild(this._overlay);

        ActivityAudio.play('oops');
    }

    destroy () {
        if (this._overlay && this._overlay.parentNode) {
            this._overlay.parentNode.removeChild(this._overlay);
        }
        this._overlay = null;
        this._dialog = null;
    }

    _createButton (className, icon, onClick) {
        const btn = document.createElement('button');
        btn.className = `activity-feedback-btn ${className}`;
        btn.textContent = icon;
        btn.addEventListener('pointerup', onClick);
        return btn;
    }

    _addConfetti () {
        const colors = ['#ff6b6b', '#ffd93d', '#6bcb77', '#4d96ff', '#ff6fff', '#a66cff'];
        for (let i = 0; i < 8; i++) {
            const piece = document.createElement('div');
            piece.className = 'activity-confetti-piece';
            piece.style.setProperty('--confetti-color', colors[i % colors.length]);
            piece.style.setProperty('--confetti-delay', `${i * 0.1}s`);
            piece.style.setProperty('--confetti-x', `${Math.random() * 100}%`);
            this._dialog.appendChild(piece);
        }
    }
}
